import { useQuery } from '@tanstack/react-query'
import { useLocation, useParams } from 'react-router'
import type { CircleColor } from '../../shared/circleColors'
import type { CircleSection, Starter } from '../../shared/sections'
import { api } from './api'

// home is your household's name in the family: what the app greets you with. The family's own name is plumbing.
// A casual circle has no households, so its home is just its name.
export type CircleKind = 'family' | 'casual'
// color: the ring around your picture while you're in it (shared/circleColors.ts).
// personal: your own space ("Me"), a circle only you are ever in. everyone: Everyone's view (id "everyone"), shown with a
// circle's pages: everything shared with you, with what you add there kept in your own space.
export type FamilySummary = {
  id: string
  name: string
  kind: CircleKind
  color: CircleColor
  home?: string
  personal?: boolean
  everyone?: boolean
}
// A room in a circle. A custom space ("The brothers") also says who's in it, by account id.
export type Space = { id: string; kind: 'everyone' | 'household' | 'custom'; name: string; members?: string[] }
export type Family = FamilySummary & {
  // What the circle was made for (none for circles older than starters), and its own section switches.
  starter: Starter | null
  sections: Record<CircleSection, boolean>
  home: { id: string; name: string } | null
  households: { id: string; name: string }[]
  spaces: Space[]
  members: {
    id: string
    name: string
    email: string
    householdId: string | null
    portrait: string | null
    // Who they are on this circle's tree, once they've said "That's me".
    personId: string | null
    // Whether you can take them out: you made the circle, or you invited them (missing from an older server).
    canRemove?: boolean
  }[]
  // Waiting to join, ones whose link ran out lately (expiresAt in the past) to send again, and ones declined lately.
  invites: Invite[]
  // People who came in through a link you made, until you say that's who you meant.
  fromLinks: { inviteId: string; invitedAs: string | null; id: string; name: string }[]
}
// An invite goes out by email, or as a link (texted, or shown as a QR code) naming who it's for.
export type Invite = {
  id: string
  via: 'email' | 'link'
  email: string | null
  name: string | null
  sentAt: number
  expiresAt: number
  person: string | null
  declined: boolean
}

// A circle as the People page's tiles show it: how many are on its tree and in the app, a few faces, and the spaces
// you're in there.
export type Circle = FamilySummary & {
  starter: Starter | null
  people: number
  members: number
  faces: { id: string; name: string; portrait: string | null }[]
  spaces: Space[]
}

// Everyone's view has a circle's pages, under /everyone instead of /circle/:id.
export const EVERYONE = 'everyone'
export const inEveryone = (pathname: string) => /^\/everyone(\/|$)/.test(pathname)

// The circle a page's address is about: Everyone under /everyone, else the :id in /circle/:id.
export function useCircleId() {
  const { id = '' } = useParams()
  return inEveryone(useLocation().pathname) ? EVERYONE : id
}

export const useFamilies = (enabled = true) =>
  useQuery({ queryKey: ['families'], queryFn: () => api<{ families: FamilySummary[] }>('/families'), enabled })

export const useFamily = (id: string, options: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ['family', id],
    queryFn: () => api<Family>(`/families/${encodeURIComponent(id)}`),
    enabled: options.enabled ?? true,
  })

export const useCircles = () =>
  useQuery({ queryKey: ['circles'], queryFn: async () => (await api<{ circles: Circle[] }>('/circles')).circles })
